import { useState } from "react"
import { toast } from "@/src/common/snackbar"
import { ConfirmDialog } from "@/src/common/custom-dialog"
import StackEditEditor from "@/src/features/jobPosting/jobPostingForm/interviewSetupStep/components/roundsConfigurationFiles/StackEditEditor"
import {
    applyPromptPolicyGlobally,
    fetchPromptForWorkspaceEdit,
    updatePromptPolicy,
} from "@/src/services/promptPolicies"
import { formatPolicyTypeName, validatePromptForPolicy } from "@/src/features/internalUsers/utils/promptPolicyUtils"
import type { PromptPolicy } from "@/src/types/promptPolicy"

import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { LoadingButton } from "@mui/lab"

type ModifyPromptPolicyActionsProps = {
    policy: PromptPolicy
    globalPolicies: Record<string, PromptPolicy>
    selectedWorkspaceId: string
    selectedWorkspaceName: string
    overrideCount: number
    onSuccess: () => void
    layout?: "inline" | "toolbar"
}

export function ModifyPromptPolicyActions({
    policy,
    globalPolicies,
    selectedWorkspaceId,
    selectedWorkspaceName,
    overrideCount,
    onSuccess,
    layout = "inline",
}: ModifyPromptPolicyActionsProps) {
    const [editorOpen, setEditorOpen] = useState(false)
    const [editorMode, setEditorMode] = useState<"workspace" | "global">("workspace")
    const [prompt, setPrompt] = useState("")
    const [isLoadingPrompt, setIsLoadingPrompt] = useState(false)
    const [confirmGlobalOpen, setConfirmGlobalOpen] = useState(false)
    const [applyConfirmText, setApplyConfirmText] = useState("")
    const [globalChangeComment, setGlobalChangeComment] = useState("")
    const [pendingGlobalContent, setPendingGlobalContent] = useState("")
    const [isSaving, setIsSaving] = useState(false)

    const APPLY_CONFIRM_PHRASE = "apply"
    const MAX_CHANGE_COMMENT_LENGTH = 500
    const isApplyConfirmed = applyConfirmText === APPLY_CONFIRM_PHRASE
    const trimmedChangeComment = globalChangeComment.trim()
    const isChangeCommentValid =
        trimmedChangeComment.length > 0 && trimmedChangeComment.length <= MAX_CHANGE_COMMENT_LENGTH

    const handleCloseGlobalConfirm = () => {
        setConfirmGlobalOpen(false)
        setApplyConfirmText("")
        setGlobalChangeComment("")
    }

    const policyTitle = formatPolicyTypeName(policy.policyType)

    const openEditor = async (mode: "workspace" | "global") => {
        setEditorMode(mode)
        setIsLoadingPrompt(true)
        try {
            if (mode === "workspace") {
                const text = await fetchPromptForWorkspaceEdit(policy.name, selectedWorkspaceId, globalPolicies)
                setPrompt(text)
            } else {
                setPrompt(policy.config?.prompt ?? "")
            }
            setEditorOpen(true)
        } catch {
            // Errors handled in service
        } finally {
            setIsLoadingPrompt(false)
        }
    }

    const handleCloseEditor = () => {
        setEditorOpen(false)
    }

    const handleCloseEditorWithSave = async (content: string) => {
        if (!content?.trim()) {
            toast.error("Prompt cannot be empty.")
            return
        }

        const validationError = validatePromptForPolicy(policy.name, content)
        if (validationError) {
            toast.error(validationError)
            return
        }

        if (editorMode === "workspace") {
            setIsSaving(true)
            try {
                await updatePromptPolicy(policy.name, {
                    workspaceId: selectedWorkspaceId,
                    config: { prompt: content },
                })
                toast.success(`"${policyTitle}" saved for workspace ${selectedWorkspaceName}`)
                setPrompt(content)
                setEditorOpen(false)
                onSuccess()
            } catch {
                // Error toast in service
            } finally {
                setIsSaving(false)
            }
            return
        }

        setPendingGlobalContent(content)
        setConfirmGlobalOpen(true)
    }

    const handleConfirmGlobalApply = async () => {
        setIsSaving(true)
        try {
            await applyPromptPolicyGlobally(policy.name, {
                config: { prompt: pendingGlobalContent },
                changeComment: trimmedChangeComment,
            })
            toast.success(`"${policyTitle}" applied for all workspaces`)
            setPrompt(pendingGlobalContent)
            handleCloseGlobalConfirm()
            setEditorOpen(false)
            onSuccess()
        } catch {
            // Error toast in service
        } finally {
            setIsSaving(false)
        }
    }

    const modifyDisabled = !selectedWorkspaceId || isLoadingPrompt

    const editButtons = (
        <>
            <Tooltip
                title={selectedWorkspaceId ? "" : "Select a workspace to edit a workspace-specific prompt"}
                arrow
            >
                <span style={layout === "toolbar" ? { width: "100%" } : undefined}>
                    <LoadingButton
                        variant="outlined"
                        size="small"
                        color="success"
                        sx={{
                            backgroundColor: "#fff",
                            ...(layout === "toolbar" ? { width: "100%" } : {}),
                        }}
                        disabled={modifyDisabled}
                        loading={isLoadingPrompt && editorMode === "workspace"}
                        onClick={() => openEditor("workspace")}
                    >
                        Modify
                    </LoadingButton>
                </span>
            </Tooltip>
            <LoadingButton
                variant="outlined"
                size="small"
                color="primary"
                sx={{
                    backgroundColor: "#fff",
                    ...(layout === "toolbar" ? { width: "100%" } : {}),
                }}
                disabled={isLoadingPrompt && editorMode === "global"}
                loading={isLoadingPrompt && editorMode === "global"}
                onClick={() => openEditor("global")}
            >
                Apply for all workspaces
            </LoadingButton>
        </>
    )

    return (
        <>
            {layout === "toolbar" ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap justifyContent="flex-end">
                    {editButtons}
                </Stack>
            ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {editButtons}
                </Stack>
            )}

            <StackEditEditor
                open={editorOpen}
                onClose={handleCloseEditor}
                initialContent={prompt}
                title={
                    editorMode === "workspace"
                        ? `${policyTitle} — ${selectedWorkspaceName}`
                        : `${policyTitle} — all workspaces`
                }
                onSave={setPrompt}
                onCloseEditor={handleCloseEditorWithSave}
            />

            <ConfirmDialog
                open={confirmGlobalOpen}
                onClose={handleCloseGlobalConfirm}
                title="Apply for all workspaces?"
                maxWidth="sm"
                content={
                    <Stack spacing={2}>
                        <Typography variant="body2">
                            This updates the global prompt for everyone and removes all workspace-specific overrides
                            for this policy.
                            {overrideCount > 0
                                ? ` This will remove ${overrideCount} workspace override${overrideCount === 1 ? "" : "s"}.`
                                : " No workspace overrides exist for this policy."}{" "}
                            This cannot be undone except by editing or reverting the global version history.
                        </Typography>
                        <TextField
                            fullWidth
                            required
                            multiline
                            minRows={2}
                            maxRows={4}
                            size="small"
                            label="Change comment"
                            placeholder="Why are you applying this global prompt change?"
                            value={globalChangeComment}
                            onChange={(e) => setGlobalChangeComment(e.target.value)}
                            inputProps={{ maxLength: MAX_CHANGE_COMMENT_LENGTH }}
                            helperText={`${trimmedChangeComment.length}/${MAX_CHANGE_COMMENT_LENGTH} characters (required)`}
                            autoComplete="off"
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label={`Type "${APPLY_CONFIRM_PHRASE}" to confirm`}
                            value={applyConfirmText}
                            onChange={(e) => setApplyConfirmText(e.target.value)}
                            autoComplete="off"
                        />
                    </Stack>
                }
                action={
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        loading={isSaving}
                        disabled={!isApplyConfirmed || !isChangeCommentValid}
                        onClick={handleConfirmGlobalApply}
                    >
                        Apply globally
                    </LoadingButton>
                }
            />
        </>
    )
}
